import { useState } from 'react';
import type { ReactNode } from 'react';
import { LayoutAnimation, Pressable, StyleSheet, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

import { Icon } from '../icons/Icon';
import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';

export type DataPanelProps = {
  header: string;
  headerActions?: ReactNode;
  beforeItemsTemplate?: ReactNode;
  defaultExpanded?: boolean;
  animated?: boolean;
  onHeaderLongPress?: () => void;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function DataPanel({
  header,
  headerActions,
  beforeItemsTemplate,
  defaultExpanded = true,
  animated = true,
  onHeaderLongPress,
  children,
  style,
  testID,
}: DataPanelProps) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggle = () => {
    if (animated) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    setIsExpanded((expanded) => !expanded);
  };

  return (
    <View
      testID={testID}
      style={[
        { backgroundColor: theme.colors.surface, borderRadius: theme.radius.md, overflow: 'hidden' },
        style,
      ]}
    >
      <View
        style={[
          styles.header,
          {
            backgroundColor: theme.colors.surfaceAlt,
            paddingVertical: theme.spacing(2),
            paddingHorizontal: theme.spacing(3),
            gap: theme.spacing(2),
          },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          aria-expanded={isExpanded}
          onPress={toggle}
          onLongPress={onHeaderLongPress}
          testID={testID === undefined ? undefined : `${testID}-toggle`}
          style={({ pressed }) => [styles.toggle, pressed && styles.pressed]}
        >
          <Text variant="label" numberOfLines={2}>
            {header}
          </Text>
        </Pressable>

        {headerActions}

        <Icon name={isExpanded ? 'chevron-down' : 'chevron-up'} size="sm" tone="muted" />
      </View>

      {beforeItemsTemplate}

      {isExpanded ? (
        <View
          testID={testID === undefined ? undefined : `${testID}-content`}
          style={styles.content}
        >
          {children}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center' },
  toggle: { flex: 1 },
  pressed: { opacity: 0.7 },
  content: { flexDirection: 'column', rowGap: 1 },
});
